import { useEffect, useState } from 'react';
import { useFeature } from 'context/growthbook/useFeature';

export const LOGO_TRIM_FLAG = 'consumer_app_logo_trim';

/**
 * How long to hold the logo back while GrowthBook loads. Matches the
 * `loadFeatures` timeout in GrowthbookAppWrapper; GrowthBook stays "not ready"
 * after a timeout or failed fetch, so without this the logo would never show.
 */
export const LOGO_FLAG_WAIT_MS = 4000;

const CLOUDINARY_UPLOAD =
  /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.*)$/i;

/**
 * Ask Cloudinary to trim the empty space around a logo.
 *
 * Many partner logos are uploaded with large transparent or white margins, so
 * they render tiny in the header. `e_trim` crops to the logo's edges when the
 * image is served; the stored file is never changed. It runs first, so any
 * transformations already in the URL (sizing, format) apply to the trimmed logo.
 *
 * Returns the URL unchanged when it isn't a Cloudinary image URL or is already trimmed.
 */
export const trimCloudinaryLogo = (url: string): string => {
  const match = url.match(CLOUDINARY_UPLOAD);
  if (!match) return url;
  const [, base, rest] = match;
  if (/(^|[/,])e_trim([:,/]|$)/.test(rest)) return url;
  return `${base}e_trim/${rest}`;
};

/**
 * Returns a function that prepares a header logo URL: trimmed when the
 * `consumer_app_logo_trim` flag is on, unchanged otherwise. A function (not a
 * value) so components rendering several logos call the hook once.
 *
 * Returns `null` for a trimmable logo while the flag is still loading, so the
 * header shows nothing rather than the untrimmed logo followed by a swap to
 * the trimmed one. Callers render no logo for `null`. After
 * `LOGO_FLAG_WAIT_MS` it gives up waiting and serves the original logo.
 */
export const useHeaderLogoUrl = (): ((url: string) => string | null) => {
  const feature = useFeature(LOGO_TRIM_FLAG);
  const on = feature?.on ?? false;
  const loading = feature?.loading ?? false;
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!loading) return undefined;
    const timer = setTimeout(() => setGaveUp(true), LOGO_FLAG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const waiting = loading && !gaveUp;

  return (url: string): string | null => {
    if (!url) return url;
    const trimmed = trimCloudinaryLogo(url);
    // Nothing to swap for non-Cloudinary or already-trimmed logos.
    if (trimmed === url) return url;
    if (waiting) return null;
    return on ? trimmed : url;
  };
};
